<template>
    <div class="home-view">
        <Sidebar />
        <div class="chartListContainer">
            <div class="projectHeader">
                <div class="projecttitle">
                    <h1>{{ projectTitle }}</h1>
                </div>
                <div class="partitionSelector" v-if="partitions.length > 0">
                    <v-select label="Select Partition" density="compact" variant="outlined" :items="partitions"
                        item-title="partitionName" item-value="partitionId" v-model="selectedPartition"
                        @update:model-value="onPartitionChange"></v-select>
                </div>
            </div>
            <div class="chartList">
                <div class="chart" v-for="(chart, index) in combinedChartsList" :key="chart.id">
                   <LineChart :chartData="chart" :groupedCharts="chart.groupedCharts ?? null" :projectId="projectId" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LineChart from '@/components/LineChart.vue';
import Sidebar from '../components/Sidebar.vue';
import { projectService } from '@/services/projectService';
import { useProjectStore } from '@/store/projectStore';
import { mapStores } from 'pinia';

export default {
    name: 'ChartList',
    components: {
        LineChart,
        Sidebar
    },
    data() {
        return {
            projectTitle: '',
            charts: [],
            projectId: null,
            partitions: [],
            selectedPartition: null
        };
    },
    computed: {
        ...mapStores(useProjectStore),

        combinedChartsList() {
            const groupMap = new Map();
            this.charts.forEach(chart => {
                if (chart.groupKey) {
                    const key = `${chart.groupKey}__${chart.timeGrouping?.toLowerCase() ?? ''}`;
                    if (!groupMap.has(key)) groupMap.set(key, []);
                    groupMap.get(key).push(chart);
                }
            });

            const processedIds = new Set();
            const finalCharts = [];

            this.charts.forEach(chart => {
                if (processedIds.has(chart.id)) return;

                let groupedCharts = null;
                if (chart.groupKey) {
                    const key = `${chart.groupKey}__${chart.timeGrouping?.toLowerCase() ?? ''}`;
                    const siblings = (groupMap.get(key) || []).filter(c => c.id !== chart.id);
                    if (siblings.length > 0) {
                        groupedCharts = siblings;
                        siblings.forEach(c => processedIds.add(c.id));
                    }
                }

                finalCharts.push({ ...chart, groupedCharts });
                processedIds.add(chart.id);
            });

            return finalCharts;
        }
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler(newId) {
                if (newId !== this.projectId) {
                    this.projectId = newId;
                    this.resetAndFetchData();
                }
            }
        }
    },
    methods: {
        async resetAndFetchData() {
            this.projectTitle = '';
            this.charts = [];
            this.partitions = [];
            this.selectedPartition = null;
            await Promise.all([
                this.getProject(),
                this.fetchAllPartitions()
            ]);
        },
        async getCharts() {
            try {
                if (!this.projectId) return;
                this.charts = await this.projectStore.fetchCharts(this.projectId);
            } catch (error) {
                console.error('Error fetching charts:', error);
                this.charts = [];
            }
        },
        async fetchAllPartitions() {
            if (!this.projectId) return;

            this.partitions = await this.projectStore.fetchPartitions(this.projectId);

            if (this.partitions.length > 0) {
                this.selectedPartition = this.partitions[0].partitionId;
                await this.onPartitionChange(this.selectedPartition);
            } else {
                this.charts = await this.projectStore.fetchCharts(this.projectId);
            }
        },
        async onPartitionChange(partitionId) {
            const partition = this.partitions.find(p => p.partitionId === partitionId);
            if (!partition) return;

            const fetchedCharts = await this.projectStore.fetchCharts(this.projectId);
            this.charts = fetchedCharts.filter(chart => partition.charts.includes(chart.id));
        },
        async getChartById(chartId) {
            return projectService.getChartById(chartId);
        },
        async getProject() {
            try {
                if (!this.projectId) return;
                const project = await this.projectStore.fetchProjectDetails(this.projectId);
                this.projectTitle = project.title;
            } catch (error) {
                console.error('Error fetching project:', error);
                this.projectTitle = '';
            }
        }
    },
    async mounted() {
        if (this.$route.params.id) {
            this.projectId = this.$route.params.id;
            await this.resetAndFetchData();
        }
    }
};
</script>

<style scoped>
.home-view {
    display: flex;
    height: 100vh;
}

.chartListContainer {
    width: calc(100% - 250px);
    height: 100vh;
    overflow: auto;
    padding: 24px;
}

.projectHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.partitionSelector {
    width: 200px;
}

.chartList {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
}

.chart {
    background: white;
    border-radius: 4px;
    box-shadow: 0.2rem 0.2rem 0.2rem 0.2rem rgba(0,0,0,0.1);
    padding: 16px;
}

.projecttitle {
    margin-bottom: 24px;
}

.projecttitle h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
}
</style>